import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, NavLink } from 'react-router-dom';
import { getMyCreations, getGenome, getFeedbackForGenome, mutateGenome, deployGenome, publishGenome } from '../api';
import AgentCard from '../components/AgentCard';
import CardGrid from '../components/CardGrid';
import styled from 'styled-components';
import { FaPlay, FaRocket, FaGlobe } from 'react-icons/fa';

// --- Styled Components (unchanged) ---
const Form = styled.form`
  background: ${props => props.theme.colors.surface};
  padding: 2rem;
  border-radius: 8px;
`;
const Section = styled.fieldset`
  border: 1px solid ${props => props.theme.colors.surface2};
  border-radius: 5px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
`;
const Legend = styled.legend`
  color: ${props => props.theme.colors.accent};
  font-weight: bold;
  font-size: 1.2rem;
  padding: 0 0.5rem;
`;
const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  background: ${props => props.theme.colors.background};
  border: 1px solid ${props => props.theme.colors.surface2};
  border-radius: 5px;
  color: ${props => props.theme.colors.text};
  box-sizing: border-box;
  margin-bottom: 1rem;
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
`;
const TextArea = styled.textarea`
  width: 100%;
  min-height: 150px;
  padding: 0.75rem;
  background: ${props => props.theme.colors.background};
  border: 1px solid ${props => props.theme.colors.surface2};
  border-radius: 5px;
  color: ${props => props.theme.colors.text};
  box-sizing: border-box;
  font-family: monospace;
  margin-bottom: 1rem;
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
`;
const SliderGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  margin-bottom: 1rem;
`;
const SliderRow = styled.div`
  display: grid;
  grid-template-columns: 120px 1fr 50px;
  align-items: center;
  gap: 1rem;
  label { color: ${props => props.theme.colors.textSecondary}; }
  input[type="range"] { flex: 1; }
  span { text-align: right; color: ${props => props.theme.colors.accent}; font-weight: 500; }
`;
const CheckboxRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
  input { width: 20px; height: 20px; }
`;
const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 5px;
  background: ${props => props.theme.colors.primary};
  color: white;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  margin-right: 1rem;
  transition: background-color 0.2s;
  &:disabled { background: ${props => props.theme.colors.textSecondary}; cursor: not-allowed; }
`;
const ErrorMsg = styled.p`
  color: ${props => props.theme.colors.error};
  background: rgba(248, 81, 73, 0.1);
  padding: 0.5rem 1rem;
  border-radius: 5px;
`;
const SuccessMsg = styled.div`
  background: rgba(86, 227, 159, 0.1);
  border-left: 4px solid ${props => props.theme.colors.accent};
  padding: 1rem;
  border-radius: 5px;
  margin-bottom: 1rem;
  h4 { margin: 0 0 0.5rem 0; color: ${props => props.theme.colors.accent}; }
`;

// --- Page-Specific Styles (unchanged) ---
const SplitPanel = styled.div`
  display: grid;
  grid-template-columns: 1fr 1.2fr;
  gap: 2rem;
`;
const Panel = styled.div`
  background: ${props => props.theme.colors.surface};
  border-radius: 8px;
  padding: 2rem;
  height: 80vh;
  overflow-y: auto;
  box-shadow: ${props => props.theme.shadow};
`;

// --- *** 'rating' prop is now '$rating' *** ---
const FeedbackCard = styled.div`
  background: ${props => props.theme.colors.background};
  border: 1px solid ${props => props.theme.colors.surface2};
  border-radius: 5px;
  padding: 1rem;
  margin-bottom: 1rem;

  .suggestion {
    border-left: 3px solid ${props => props.theme.colors.accent};
    padding-left: 10px;
    margin-top: 1rem;
    font-weight: bold;
    font-size: 0.9rem;
  }
  p {
    margin: 0.5rem 0;
    font-size: 0.9rem;
    color: ${props => props.theme.colors.textSecondary};
  }
  .rating {
    font-weight: bold;
    text-transform: capitalize;
    color: ${props => props.$rating === 'bad' ? props.theme.colors.error : props.theme.colors.accent};
  }
`;
// --- *** END OF FIX *** ---

// --- GenomeMutateForm Component ---
const GenomeMutateForm = ({ genome, onMutate, isMutating }) => {
  const [formData, setFormData] = useState(genome);

  useEffect(() => {
    setFormData(genome);
  }, [genome]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name.includes('.')) {
      const parts = name.split('.');
      setFormData(prev => {
        let newForm = JSON.parse(JSON.stringify(prev));
        let current = newForm;
        for (let i = 0; i < parts.length - 1; i++) {
          current = current[parts[i]];
        }
        current[parts[parts.length - 1]] = type === 'checkbox' ? checked : value;
        return newForm;
      });
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };
  
  const handleConfigChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      config: {
        ...prev.config,
        generationConfig: {
          ...prev.config.generationConfig,
          [name]: parseFloat(value)
        }
      }
    }));
  };
  
  const handleContextArrayChange = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      context: {
        ...prev.context,
        [section]: {
          ...prev.context[section],
          [field]: value.split('\n')
        }
      }
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onMutate(formData);
  };

  const isQuickMode = !!formData.systemPromptTemplate;

  return (
    <Form onSubmit={handleSubmit}>
      <Section>
        <Legend>1. Manifest</Legend>
        <Input name="manifest.name" value={formData.manifest.name} onChange={handleChange} />
        <Input name="manifest.purpose" value={formData.manifest.purpose} onChange={handleChange} />
      </Section>
      
      {isQuickMode ? (
        <Section>
          <Legend>2. System Prompt</Legend>
          <TextArea 
            name="systemPromptTemplate"
            value={formData.systemPromptTemplate}
            onChange={handleChange}
          />
        </Section>
      ) : (
        <Section>
          <Legend>2. Context (Architect Mode)</Legend>
          {/* --- *** THIS IS THE FIX *** --- */}
          <Input name="context.objectives.goal" placeholder="Primary Goal" value={formData.context.objectives.goal || ''} onChange={handleChange} />
          {/* --- *** END OF FIX *** --- */}
          <Input name="context.persona.role" placeholder="Role" value={formData.context.persona.role || ''} onChange={handleChange} />
          <Input name="context.persona.tone" placeholder="Tone" value={formData.context.persona.tone || ''} onChange={handleChange} />
          <Input name="context.persona.style" placeholder="Style" value={formData.context.persona.style || ''} onChange={handleChange} />
          <TextArea 
            placeholder="Beliefs (one per line)" 
            value={formData.context.objectives.beliefs.join('\n')} 
            onChange={(e) => handleContextArrayChange('objectives', 'beliefs', e.target.value)} 
          />
          <TextArea 
            placeholder="Rules (one per line)" 
            value={formData.context.guardrails.rules.join('\n')} 
            onChange={(e) => handleContextArrayChange('guardrails', 'rules', e.target.value)} 
          />
        </Section>
      )}
      
      <Section>
        <Legend>3. Configuration</Legend>
        <SliderGroup>
          <SliderRow>
            <label>Temperature</label>
            {/* --- *** UPDATED SLIDER *** --- */}
            <input type="range" name="temperature" min="0" max="2" step="0.1" value={formData.config.generationConfig.temperature} onChange={handleConfigChange} />
            <span>{formData.config.generationConfig.temperature}</span>
          </SliderRow>
          <SliderRow>
            <label>Top K</label>
            {/* --- *** UPDATED SLIDER *** --- */}
            <input type="range" name="topK" min="1" max="64" step="1" value={formData.config.generationConfig.topK} onChange={handleConfigChange} />
            <span>{formData.config.generationConfig.topK}</span>
          </SliderRow>
          <SliderRow>
            <label>Max Tokens</label>
            <input type="range" name="maxOutputTokens" min="512" max="8192" step="512" value={formData.config.generationConfig.maxOutputTokens} onChange={handleConfigChange} />
            <span>{formData.config.generationConfig.maxOutputTokens}</span>
          </SliderRow>
        </SliderGroup>
      </Section>
      
      {!isQuickMode && (
        <Section>
          <Legend>4. Evolution</Legend>
          <CheckboxRow>
            <input type="checkbox" id="automutate" name="lineage.automutate" checked={formData.lineage.automutate} onChange={handleChange} />
            <label htmlFor="automutate">Enable Auto-Mutation</label>
          </CheckboxRow>
          {formData.lineage.automutate && (
            <Input 
              type="number" 
              name="fitness.mutateOn" 
              min="1"
              value={formData.fitness.mutateOn} 
              onChange={handleChange} 
            />
          )}
        </Section>
      )}
      
      <Button type="submit" disabled={isMutating}>
        {isMutating ? 'Mutating...' : 'Evolve to v' + (genome.lineage.version + 1)}
      </Button>
    </Form>
  );
};


// --- EvolvePage Component (Button logic fixed) ---
const EvolvePage = () => {
  const { genomeId } = useParams();
  const navigate = useNavigate();
  
  const [creations, setCreations] = useState([]);
  const [genome, setGenome] = useState(null);
  const [feedback, setFeedback] = useState([]);
  const [newV2Genome, setNewV2Genome] = useState(null); 
  const [deployedV2Genome, setDeployedV2Genome] = useState(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isMutating, setIsMutating] = useState(false);
  const [isDeploying, setIsDeploying] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (!genomeId) {
      setLoading(true);
      getMyCreations()
        .then(res => setCreations(res.data))
        .catch(err => setError("Failed to fetch creations"))
        .finally(() => setLoading(false));
    } else {
      setLoading(true);
      setNewV2Genome(null);
      setDeployedV2Genome(null);
      setSuccess('');
      setError('');
      Promise.all([
        getGenome(genomeId),
        getFeedbackForGenome(genomeId)
      ])
      .then(([genomeRes, feedbackRes]) => {
        setGenome(genomeRes.data);
        setFeedback(feedbackRes.data);
      })
      .catch(err => setError("Failed to load genome details"))
      .finally(() => setLoading(false));
    }
  }, [genomeId]);

  const handleMutate = async (formData) => {
    setIsMutating(true);
    setError('');
    setSuccess('');
    try {
      const { data } = await mutateGenome(genomeId, formData);
      setNewV2Genome(data);
      setSuccess("Mutation Successful! Ready to deploy v" + data.lineage.version);
    } catch (err) {
      setError("Failed to mutate: " + err.message);
    } finally {
      setIsMutating(false);
    }
  };

  const handleDeployV2 = async () => {
    if (!newV2Genome) return;
    setIsDeploying(true);
    setError('');
    setSuccess('');
    try {
      const { data } = await deployGenome(newV2Genome.metadata.genomeId);
      setDeployedV2Genome(data);
      setSuccess("New version deployed!");
    } catch (err) {
      setError("Failed to deploy new version: " + err.message);
    } finally {
      setIsDeploying(false);
    }
  };
  
  const handlePublishV2 = async () => {
    if (!deployedV2Genome) return;
    setIsPublishing(true);
    setError('');
    setSuccess('');
    try {
      await publishGenome(deployedV2Genome.metadata.genomeId);
      setSuccess("New version published to gallery!");
    } catch (err) {
      setError("Failed to publish new version: " + err.message);
    } finally {
      setIsPublishing(false);
    }
  };

  if (loading && !genome) {
    return <div>Loading...</div>;
  }
  
  if (error) return <ErrorMsg>{error}</ErrorMsg>;

  if (!genomeId) {
    // RENDER LIST MODE
    return (
      <div>
        <h2>Evolve an Agent</h2>
        <p>Select a genome to view its feedback and mutate it.</p>
        <CardGrid>
          {creations.map(g => (
            <AgentCard 
              key={g.metadata.genomeId} 
              genome={g}
              onClick={() => navigate(`/evolve/${g.metadata.genomeId}`)}
            />
          ))}
        </CardGrid>
      </div>
    );
  }
  
  if (loading || !genome) {
    return <div>Loading Genome Details...</div>;
  }
  
  // RENDER DETAIL (SPLIT-PANEL) MODE
  return (
    <div>
      <NavLink to="/evolve" style={{color: 'white', textDecoration: 'none'}}>&larr; Back to List</NavLink>
      <h2 style={{marginTop: '1rem'}}>Evolving: {genome.manifest.name} (v{genome.lineage.version})</h2>
      
      {error && <ErrorMsg>{error}</ErrorMsg>}
      {success && <SuccessMsg>{success}</SuccessMsg>}
      
      {/* State 1: Mutated, but not deployed */}
      {newV2Genome && !deployedV2Genome && (
        <SuccessMsg>
          <h4><FaRocket /> Mutation Successful!</h4>
          <p>Created v{newV2Genome.lineage.version}. Ready to deploy.</p>
          <Button onClick={handleDeployV2} disabled={isDeploying}>
            {isDeploying ? 'Deploying...' : 'Deploy New Version'}
          </Button>
        </SuccessMsg>
      )}

      {/* State 2: Mutated AND Deployed */}
      {deployedV2Genome && (
        <SuccessMsg>
          <h4><FaRocket /> New Version Deployed!</h4>
          <p>v{deployedV2Genome.lineage.version} is now live.</p>
          <Button onClick={() => navigate(`/test/${deployedV2Genome.metadata.genomeId}`)}>
            <FaPlay /> Test New Version
          </Button>
          <Button onClick={handlePublishV2} disabled={isPublishing}>
            <FaGlobe /> {isPublishing ? 'Publishing...' : 'Publish to Gallery'}
          </Button>
        </SuccessMsg>
      )}
      
      <SplitPanel>
        <Panel>
          <h3>Feedback & Suggestions</h3>
          {feedback.length === 0 && <p>No feedback received for this genome yet.</p>}
          {feedback.map(fb => (
            <FeedbackCard key={fb.receivedAt} $rating={fb.feedbackData.rating}>
              <div className="suggestion">{fb.suggestion || "No suggestion provided by agent."}</div>
              <p style={{marginTop: '1rem'}}>
                <strong>Input:</strong> {fb.feedbackData.user_input}<br />
                <strong>Rating:</strong> <span className="rating">{fb.feedbackData.rating}</span><br />
                <strong>Comment:</strong> {fb.feedbackData.comment}
              </p>
            </FeedbackCard>
          ))}
        </Panel>
        
        <Panel>
          <h3>Mutate Genome (v{genome.lineage.version + 1})</h3>
          
          {/* State 3: Hide form after mutation is complete */}
          {!newV2Genome ? (
            <GenomeMutateForm 
              genome={genome} 
              onMutate={handleMutate} 
              isMutating={isMutating} 
            />
          ) : (
            <div>
              <p>This genome has been evolved.</p>
              <Button onClick={() => navigate(`/evolve/${newV2Genome.metadata.genomeId}`)}>
                Evolve v{newV2Genome.lineage.version}
              </Button>
            </div>
          )}
          
        </Panel>
      </SplitPanel>
    </div>
  );
};

export default EvolvePage;